import React from "react";
import { chatMessengerShow } from "@origin-digital/event-dispatcher";
import {
  Box,
  Button,
  Card,
  Column,
  Columns,
  Divider,
  Heading,
  Stack,
  StackSection,
  Text,
} from "@origin-digital/ods-core";
import { IconLiveChat, IconPhone } from "@origin-digital/ods-icons";
import { alert } from "@origin-digital/reporting-client";
import {
  shouldSendOfferTabAnalytics,
  formatOfferTabForAnalytics,
  IOfferTab,
  IOfferTabAnalytics,
  OfferTabAnalytics,
  OfferType,
} from "../Offers/offerTab";
import content from "./content.json";
import { useAppContext } from "../../context/AppContext";
import { APP_NAME } from "../../consts";

interface IOfferTypeUnavailableProps {
  type: OfferType;
  bothUndeliverable?: boolean;
  offerTab?: IOfferTab;
  analyticsSent?: IOfferTabAnalytics;
  setAnalyticsSent?: (key: IOfferTabAnalytics) => void;
}

export const OfferTypeUnavailable = ({
  type,
  bothUndeliverable,
  offerTab,
  analyticsSent,
  setAnalyticsSent,
}: IOfferTypeUnavailableProps) => {
  const { context } = useAppContext();
  const suburb = context.lookupAddress?.qasAddress?.city ?? "";

  const subtitle = bothUndeliverable
    ? content.subtitleBoth
    : type === OfferType.REFILL
    ? content.subtitleRefill
    : content.subtitleExchange;

  if (
    offerTab &&
    setAnalyticsSent &&
    shouldSendOfferTabAnalytics(offerTab, analyticsSent)
  ) {
    setAnalyticsSent(OfferTabAnalytics(offerTab, analyticsSent));
    alert.base("notification", {
      appName: APP_NAME,
      alertMessage: subtitle,
      alertCode: formatOfferTabForAnalytics(offerTab),
    });
  }

  return (
    <StackSection width="large" data-id="offer-type-unavailable-section">
      <Card padding="medium" data-id="offer-type-unavailable-card">
        <Stack space="xlarge" data-id="offer-type-unavailable-content">
          {/* Header */}
          <Columns direction={{ xs: "column", lg: "row-reverse" }}>
            <Column width="content">
              <Box
                display="flex"
                marginBottom={{ xs: "large", lg: "none" }}
                marginLeft={{ xs: "none", lg: "large" }}
              >
                <img
                  src={content.imageUrl}
                  width="96"
                  height="96"
                  alt={content.imageAlt}
                  data-id="offer-type-unavailable-image"
                />
              </Box>
            </Column>
            <Column>
              <Stack space="xsmall">
                <Text
                  variant="subtitle"
                  data-id="offer-type-unavailable-subtitle"
                >
                  {subtitle}
                </Text>
                <Heading variant="h2" data-id="offer-type-unavailable-title">
                  {suburb}
                </Heading>
              </Stack>
            </Column>
          </Columns>

          <Divider />

          {/* CTA */}
          <Stack space="large">
            <Text
              variant="subtitle-small"
              data-id="offer-type-unavailable-cta-text"
            >
              {content.ctaText}
            </Text>
            <Columns space="medium">
              <Column width="content">
                <Button
                  data-id="offer-type-unavailable-chat-btn"
                  variant="contained"
                  icon={<IconLiveChat color="neutralDark" />}
                  iconPosition="leading"
                  onClick={() => chatMessengerShow({})}
                >
                  {content.chatButtonText}
                </Button>
              </Column>
              <Column width="content">
                <Button
                  data-id="offer-type-unavailable-phone-btn"
                  variant="outlined"
                  icon={<IconPhone />}
                  iconPosition="leading"
                  component="a"
                  href={content.phoneHref}
                >
                  {content.phoneButtonText}
                </Button>
              </Column>
            </Columns>
          </Stack>
        </Stack>
      </Card>
    </StackSection>
  );
};
